import { createElement } from '../../../core/dom/helpers.js';
import { createDatePicker } from '../../../components/date-picker/date-picker.js';

/** @type {import('../../../contracts/filters.js').FilterDriver} */
export const daterangeFilterDriver = {
  type: 'daterange',
  mount(container, ctx) {
    const { def, onChange } = ctx;

    const initFrom = Array.isArray(ctx.value) && typeof ctx.value[0] === 'string' ? ctx.value[0] : '';
    const initTo = Array.isArray(ctx.value) && typeof ctx.value[1] === 'string' ? ctx.value[1] : '';

    if (def.customPicker) {
      if (!ctx.localeConfig) {
        throw new Error('daterange-filter-driver: customPicker=true requires ctx.localeConfig');
      }
      const wrap = createElement('div', { class: 'daterange-inputs' });
      container.appendChild(wrap);
      let from = initFrom;
      let to = initTo;
      // Each picker gets an explicit ariaLabel because the visible
      // <label for="filter-${def.key}"> in filter-bar.js associates with
      // `${def.key}`, NOT with the suffixed `_from`/`_to` ids. Without this,
      // SRs would announce both pickers identically (or as unlabeled buttons).
      const fromHandle = createDatePicker(wrap, {
        id: `filter-${def.key}_from`,
        value: from,
        placeholder: def.placeholder,
        localeConfig: ctx.localeConfig,
        ariaLabel: `${def.label} from`,
        onChange: (v) => { from = v; onChange([from, to]); },
      });
      const toHandle = createDatePicker(wrap, {
        id: `filter-${def.key}_to`,
        value: to,
        placeholder: def.placeholder,
        localeConfig: ctx.localeConfig,
        ariaLabel: `${def.label} to`,
        onChange: (v) => { to = v; onChange([from, to]); },
      });
      return {
        setValue(value) {
          if (Array.isArray(value) && value.length === 2) {
            from = typeof value[0] === 'string' ? value[0] : '';
            to = typeof value[1] === 'string' ? value[1] : '';
            fromHandle.setValue(from);
            toHandle.setValue(to);
          }
        },
        getValue() {
          return [from, to];
        },
        destroy() {
          fromHandle.destroy();
          toHandle.destroy();
          wrap.remove();
        },
      };
    }

    // Native path — unchanged behavior, but now consumes ctx.value (URL hydration
    // is added in filter-bar.js for the registry path).
    const rangeWrapper = createElement('div', { class: 'daterange-inputs' });

    const fromInput = /** @type {HTMLInputElement} */ (createElement('input', {
      type: 'date',
      id: `filter-${def.key}_from`,
      name: `${def.key}_from`,
    }));

    const toInput = /** @type {HTMLInputElement} */ (createElement('input', {
      type: 'date',
      id: `filter-${def.key}_to`,
      name: `${def.key}_to`,
    }));

    if (def.placeholder) {
      fromInput.setAttribute('placeholder', def.placeholder);
      toInput.setAttribute('placeholder', def.placeholder);
    }

    if (initFrom) fromInput.value = initFrom;
    if (initTo) toInput.value = initTo;

    fromInput.addEventListener('change', () => onChange(getValues()));
    toInput.addEventListener('change', () => onChange(getValues()));

    rangeWrapper.appendChild(fromInput);
    rangeWrapper.appendChild(toInput);
    container.appendChild(rangeWrapper);

    /** @returns {string[]} Two-element array: [fromValue, toValue] */
    function getValues() {
      return [fromInput.value, toInput.value];
    }

    return {
      setValue(value) {
        if (Array.isArray(value) && value.length === 2) {
          fromInput.value = typeof value[0] === 'string' ? value[0] : '';
          toInput.value = typeof value[1] === 'string' ? value[1] : '';
        }
      },
      getValue() {
        return getValues();
      },
      destroy() {
        rangeWrapper.remove();
      },
    };
  },
};
